import {
  Box,
  Flex,
  Icon,
  Image,
  Stack,
  Text,
  VStack,
  Grid,
} from "@chakra-ui/react";
import React from "react";
import { MdCheckCircle } from "react-icons/md";
import { studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import QuestionResourceActions from "./question-resource-actions";

const ACADEMIC = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  text: studentSurfaces.text,
};

const darkTextSx = { "html[data-theme='dark'] &": { color: "var(--text-primary)" } };
const darkQuestionHtmlSx = {
  "html[data-theme='dark'] &": { color: "var(--text-primary)" },
  "html[data-theme='dark'] & p, html[data-theme='dark'] & span, html[data-theme='dark'] & div, html[data-theme='dark'] & li":
    { color: "inherit" },
};
const darkChoiceSx = {
  "html[data-theme='dark'] &": {
    bg: "var(--surface-muted)",
    borderColor: "var(--border-subtle)",
  },
};
const darkChoiceSelectedSx = {
  "html[data-theme='dark'] &": {
    bg: "rgba(var(--brand-primary-rgb), 0.2)",
    borderColor: "var(--border-subtle)",
  },
};

const ExamQuestions = ({
  data,
  answers,
  setAnswers,
  selectedQuestionIdx,
  design = "default",
  questionSerifFont,
  examType,
}) => {
  const baseUrl = "https://edutopiaonline.com/api";
  const isAcademic = design === "academic";
  const qFont = questionSerifFont || "inherit";

  return (
    <VStack
      w="100%"
      spacing={isAcademic ? "24px" : "20px"}
      h="100%"
      onSelect={(e) => e.preventDefault()}
    >
      {/* question */}
      <Box
        w="100%"
        bg={isAcademic ? studentSurfaces.raised : "white"}
        border="1px solid"
        borderColor={isAcademic ? ACADEMIC.border : "gray.300"}
        borderRadius={isAcademic ? "16px" : "12px"}
        p={isAcademic ? { base: 6, md: 10 } : 6}
        boxShadow={isAcademic ? "sm" : "0 2px 8px rgba(0,0,0,0.08)"}
        position="relative"
        overflow="hidden"
      >
        {isAcademic ? (
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            w="8px"
            bg={ACADEMIC.primary}
            borderLeftRadius="16px"
          />
        ) : null}
        <Stack
          flexDirection={{ base: "column", lg: "row" }}
          spacing={{ base: 4, lg: 6 }}
          alignItems="flex-start"
        >
          <VStack 
            w={{ base: "100%", lg: data?.image ? "60%" : "100%" }} 
            alignItems="flex-start"
            spacing={4}
          >
            {data?.comprehension && (
              <Box w="100%">
                <Text
                  fontSize="18px"
                  fontWeight="700"
                  textDecoration="underline"
                  mb={2}
                  color="gray.800"
                >
                  Comprehension
                </Text>
                <Text 
                  fontSize="16px" 
                  color="gray.700"
                  lineHeight="1.6"
                  whiteSpace="pre-wrap"
                >
                  {data?.comprehension}
                </Text>
              </Box>
            )}
            <Box
              w="100%"
              onMouseDown={(e) => e.preventDefault()}
              pl={isAcademic ? { base: 2, md: 4 } : 0}
            >
              {!isAcademic ? (
                <Text
                  fontSize="18px"
                  fontWeight="700"
                  textDecoration="underline"
                  mb={2}
                  color="gray.800"
                >
                  Question
                </Text>
              ) : null}
              <Box
                fontSize={isAcademic ? { base: "xl", md: "2xl" } : "16px"}
                color={isAcademic ? ACADEMIC.text : "gray.800"}
                lineHeight="1.65"
                fontFamily={isAcademic ? qFont : "inherit"}
                fontWeight={isAcademic ? 400 : undefined}
                sx={{
                  "& p": {
                    margin: 0,
                  },
                  "& *": {
                    maxWidth: "100%",
                  },
                  ...(isAcademic ? darkQuestionHtmlSx : {}),
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: data?.question }} />
              </Box>
            </Box>
          </VStack>

          {data?.image && (
            <Box flex={1} display="flex" justifyContent="center" alignItems="flex-start">
              <Image
                src={`${baseUrl}/${data?.image}`}
                w={{ base: "100%", lg: "auto" }}
                maxH="400px"
                objectFit="contain"
                borderRadius="8px"
              />
            </Box>
          )}
        </Stack>
      </Box>

      <QuestionResourceActions
        question={data}
        variant="solve"
        examType={examType ?? data?.exam_type ?? data?.exam?.type}
        design={design}
      />

      {/* answers */}
      <Box w="100%">
        <Grid
          role="group"
          aria-labelledby="my-radio-group"
          templateColumns={
            isAcademic
              ? "repeat(2, minmax(0, 1fr))"
              : { base: "1fr", lg: "repeat(2, 1fr)" }
          }
          gap={isAcademic ? 4 : 3}
          w="100%"
        >
          {data?.answers?.map((val, index) => {
            const letter = String.fromCharCode(65 + index); // A, B, C, D
            const isSelected = answers[selectedQuestionIdx]?.answer_id == val?.id;

            return (
              <Box key={val?.id} w="100%">
                <label
                  htmlFor={`answer_${selectedQuestionIdx}_${index}`}
                  style={{
                    display: "block",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  <Flex
                    w="100%"
                    alignItems="center"
                    p={isAcademic ? 6 : 4}
                    borderRadius={isAcademic ? "12px" : "12px"}
                    border={isAcademic ? "2px solid" : "2px solid"}
                    borderColor={
                      isAcademic
                        ? isSelected
                          ? ACADEMIC.primary
                          : ACADEMIC.border
                        : isSelected
                          ? "main_1"
                          : "gray.300"
                    }
                    bg={
                      isAcademic
                        ? isSelected
                          ? ACADEMIC.cream
                          : studentSurfaces.raised
                        : isSelected
                          ? "blue.50"
                          : "white"
                    }
                    boxShadow={
                      isSelected
                        ? "0 4px 12px rgba(91, 13, 22,0.12)"
                        : isAcademic
                          ? "none"
                          : "0 2px 4px rgba(0,0,0,0.1)"
                    }
                    transition="all 0.2s"
                    _hover={{
                      borderColor: isAcademic ? ACADEMIC.primary : "main_1",
                      bg: isAcademic ? "rgba(247,242,236,0.65)" : undefined,
                    }}
                    sx={
                      isAcademic
                        ? isSelected
                          ? darkChoiceSelectedSx
                          : darkChoiceSx
                        : undefined
                    }
                  >
                    <Box position="relative" mr={isAcademic ? 6 : 3} flexShrink={0}>
                      <input
                        type="radio"
                        value={val?.id}
                        name={`answer_${selectedQuestionIdx}`}
                        id={`answer_${selectedQuestionIdx}_${index}`}
                        style={{
                          position: "absolute",
                          opacity: 0,
                          width: "40px",
                          height: "40px",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                        onChange={(e) => {
                          const formData = {
                            question_id: data?.id,
                            answer_id: e?.target?.value,
                          };
                          const next = [...answers];
                          next[selectedQuestionIdx] = formData;
                          setAnswers(next);
                        }}
                        checked={isSelected}
                      />
                      <Box
                        as="span"
                        w={isAcademic ? "40px" : "40px"}
                        h={isAcademic ? "40px" : "40px"}
                        borderRadius="full"
                        border="2px solid"
                        borderColor={
                          isAcademic
                            ? isSelected
                              ? ACADEMIC.primary
                              : ACADEMIC.border
                            : isSelected
                              ? "main_1"
                              : "gray.400"
                        }
                        bg={
                          isAcademic
                            ? isSelected
                              ? ACADEMIC.primary
                              : studentSurfaces.muted
                            : isSelected
                              ? "main_1"
                              : "white"
                        }
                        color={isSelected ? "white" : isAcademic ? ACADEMIC.primary : "gray.700"}
                        sx={
                          isAcademic && !isSelected
                            ? { "html[data-theme='dark'] &": { color: "var(--text-primary)" } }
                            : undefined
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        fontSize="18px"
                        position="relative"
                        zIndex={1}
                        transition="all 0.2s"
                      >
                        {letter}
                      </Box>
                    </Box>
                    <Text
                      fontSize={isAcademic ? "lg" : "18px"}
                      flex={1}
                      lineHeight="1.5"
                      fontWeight={isAcademic && isSelected ? 700 : isAcademic ? 500 : undefined}
                      color={isAcademic ? (isSelected ? ACADEMIC.primary : ACADEMIC.text) : "inherit"}
                      sx={isAcademic ? darkTextSx : undefined}
                    >
                      {val?.answer}
                    </Text>
                    {isAcademic && isSelected ? (
                      <Icon as={MdCheckCircle} boxSize={6} color={ACADEMIC.primary} ml={2} flexShrink={0} />
                    ) : null}
                  </Flex>
                </label>
              </Box>
            );
          })}
        </Grid>
      </Box>
    </VStack>
  );
};

export default ExamQuestions;
